'use client';

import { useEffect, useRef } from 'react';
import * as THREE from 'three';

/**
 * Animated hero background — adapted from a raymarched metaball shader demo, recolored to the
 * site's light theme (#F7F9FF background, #195AFF/#53B2FF accents) and stripped down to just
 * the animation: no debug panel, no demo copy/UI, no fixed-corner decoration.
 *
 * Mounted as a progressive enhancement OVER HomeHero's existing plain radial-gradient div, not
 * instead of it — if WebGL is unavailable or the visitor prefers reduced motion, that gradient
 * is all that shows, so there's no regression path.
 */
export default function MetaballHeroBackground() {
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;

    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(
      navigator.userAgent
    );
    const isLowPowerDevice = isMobile || (navigator.hardwareConcurrency ?? 8) <= 4;
    const devicePixelRatio = Math.min(window.devicePixelRatio || 1, isMobile ? 1.5 : 2);

    const settings = {
      sphereCount: isMobile ? 3 : 5,
      ambientIntensity: 0.4,
      diffuseIntensity: 0.5,
      specularIntensity: 1.3,
      specularPower: 10,
      fresnelPower: 1.6,
      backgroundColor: new THREE.Color(0xf7f9ff),
      sphereColor: new THREE.Color(0xdbe6ff),
      lightColor: new THREE.Color(0x195aff),
      lightPosition: new THREE.Vector3(0.8, 1, 0.8),
      smoothness: 0.35,
      contrast: 1.15,
      fogDensity: 0.1,
      animationSpeed: 0.45,
      movementScale: 1.0,
      mergeDistance: 1.4,
      cursorRadiusMin: 0.08,
      cursorRadiusMax: 0.14,
      cursorGlowIntensity: 0.5,
      cursorGlowRadius: 1.2,
      cursorGlowColor: new THREE.Color(0x53b2ff),
    };

    const scene = new THREE.Scene();
    const camera = new THREE.OrthographicCamera(-1, 1, 1, -1, 0.1, 10);
    camera.position.z = 1;
    const clock = new THREE.Clock();

    const renderer = new THREE.WebGLRenderer({
      antialias: !isLowPowerDevice,
      alpha: true,
      powerPreference: isMobile ? 'default' : 'high-performance',
    });
    renderer.setPixelRatio(devicePixelRatio);
    renderer.setClearColor(0x000000, 0);
    renderer.outputColorSpace = THREE.SRGBColorSpace;

    const canvas = renderer.domElement;
    canvas.style.cssText = 'position:absolute;inset:0;width:100%;height:100%;display:block;';
    container.appendChild(canvas);

    let width = container.clientWidth || 1;
    let height = container.clientHeight || 1;
    renderer.setSize(width, height);

    const mousePosition = new THREE.Vector2(0.5, 0.5);
    const targetMousePosition = new THREE.Vector2(0.5, 0.5);
    const cursorSphere3D = new THREE.Vector3(0, 0, 0);

    const material = new THREE.ShaderMaterial({
      uniforms: {
        uTime: { value: 0 },
        uResolution: { value: new THREE.Vector2(width, height) },
        uCursorSphere: { value: new THREE.Vector3(0, 0, 0) },
        uCursorRadius: { value: settings.cursorRadiusMin },
        uSphereCount: { value: settings.sphereCount },
        uMergeDistance: { value: settings.mergeDistance },
        uSmoothness: { value: settings.smoothness },
        uAmbientIntensity: { value: settings.ambientIntensity },
        uDiffuseIntensity: { value: settings.diffuseIntensity },
        uSpecularIntensity: { value: settings.specularIntensity },
        uSpecularPower: { value: settings.specularPower },
        uFresnelPower: { value: settings.fresnelPower },
        uBackgroundColor: { value: settings.backgroundColor },
        uSphereColor: { value: settings.sphereColor },
        uLightColor: { value: settings.lightColor },
        uLightPosition: { value: settings.lightPosition },
        uContrast: { value: settings.contrast },
        uFogDensity: { value: settings.fogDensity },
        uAnimationSpeed: { value: settings.animationSpeed },
        uMovementScale: { value: settings.movementScale },
        uCursorGlowIntensity: { value: settings.cursorGlowIntensity },
        uCursorGlowRadius: { value: settings.cursorGlowRadius },
        uCursorGlowColor: { value: settings.cursorGlowColor },
        uIsLowPower: { value: isLowPowerDevice ? 1.0 : 0.0 },
      },
      vertexShader: `
        void main() {
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: `
        precision ${isLowPowerDevice ? 'mediump' : 'highp'} float;
        uniform float uTime;
        uniform vec2 uResolution;
        uniform vec3 uCursorSphere;
        uniform float uCursorRadius;
        uniform int uSphereCount;
        uniform float uMergeDistance;
        uniform float uSmoothness;
        uniform float uAmbientIntensity;
        uniform float uDiffuseIntensity;
        uniform float uSpecularIntensity;
        uniform float uSpecularPower;
        uniform float uFresnelPower;
        uniform vec3 uBackgroundColor;
        uniform vec3 uSphereColor;
        uniform vec3 uLightColor;
        uniform vec3 uLightPosition;
        uniform float uContrast;
        uniform float uFogDensity;
        uniform float uAnimationSpeed;
        uniform float uMovementScale;
        uniform float uCursorGlowIntensity;
        uniform float uCursorGlowRadius;
        uniform vec3 uCursorGlowColor;
        uniform float uIsLowPower;

        const float PI = 3.14159265359;
        const float EPSILON = 0.001;

        float smin(float a, float b, float k) {
          float h = max(k - abs(a - b), 0.0) / k;
          return min(a, b) - h * h * k * 0.25;
        }

        float sdSphere(vec3 p, float r) {
          return length(p) - r;
        }

        float sceneSDF(vec3 pos) {
          float result = 100.0;
          float t = uTime * uAnimationSpeed;

          int maxIter = uIsLowPower > 0.5 ? 4 : min(uSphereCount, 8);
          for (int i = 0; i < 8; i++) {
            if (i >= uSphereCount || i >= maxIter) break;
            float fi = float(i);
            float radius = 0.16 + mod(fi, 3.0) * 0.07;
            float orbitRadius = (0.35 + mod(fi, 3.0) * 0.18) * uMovementScale;
            float phaseOffset = fi * PI * 0.4;
            float speed = 0.4 + fi * 0.12;

            vec3 offset = vec3(
              sin(t * speed + phaseOffset) * orbitRadius,
              cos(t * speed * 0.85 + phaseOffset * 1.3) * orbitRadius * 0.7,
              sin(t * speed * 0.5 + phaseOffset) * 0.3
            );

            vec3 toCursor = uCursorSphere - offset;
            float cursorDist = length(toCursor);
            if (cursorDist < uMergeDistance && cursorDist > 0.0) {
              float attraction = (1.0 - cursorDist / uMergeDistance) * 0.3;
              offset += normalize(toCursor) * attraction;
            }

            float movingSphere = sdSphere(pos - offset, radius);
            float blend = 0.06;
            if (cursorDist < uMergeDistance) {
              float influence = 1.0 - (cursorDist / uMergeDistance);
              blend = mix(0.06, uSmoothness, influence * influence * influence);
            }
            result = smin(result, movingSphere, blend);
          }

          float cursorBall = sdSphere(pos - uCursorSphere, uCursorRadius);
          result = smin(result, cursorBall, uSmoothness);
          return result;
        }

        vec3 calcNormal(vec3 p) {
          float eps = 0.001;
          return normalize(vec3(
            sceneSDF(p + vec3(eps, 0, 0)) - sceneSDF(p - vec3(eps, 0, 0)),
            sceneSDF(p + vec3(0, eps, 0)) - sceneSDF(p - vec3(0, eps, 0)),
            sceneSDF(p + vec3(0, 0, eps)) - sceneSDF(p - vec3(0, 0, eps))
          ));
        }

        float softShadow(vec3 ro, vec3 rd, float mint, float maxt, float k) {
          float result = 1.0;
          float t = mint;
          int steps = uIsLowPower > 0.5 ? 6 : 16;
          for (int i = 0; i < 16; i++) {
            if (i >= steps || t >= maxt) break;
            float h = sceneSDF(ro + rd * t);
            if (h < EPSILON) return 0.0;
            result = min(result, k * h / t);
            t += h;
          }
          return result;
        }

        float rayMarch(vec3 ro, vec3 rd) {
          float t = 0.0;
          int maxSteps = uIsLowPower > 0.5 ? 20 : 40;
          for (int i = 0; i < 40; i++) {
            if (i >= maxSteps) break;
            vec3 p = ro + rd * t;
            float d = sceneSDF(p);
            if (d < EPSILON) return t;
            if (t > 5.0) break;
            t += d * 0.9;
          }
          return -1.0;
        }

        vec3 lighting(vec3 p, vec3 rd) {
          vec3 normal = calcNormal(p);
          vec3 viewDir = -rd;
          vec3 lightDir = normalize(uLightPosition);
          float diff = max(dot(normal, lightDir), 0.0);
          float shadow = softShadow(p, lightDir, 0.02, 6.0, 16.0);

          vec3 ambient = uLightColor * uAmbientIntensity;
          vec3 diffuse = uLightColor * diff * uDiffuseIntensity * shadow;

          vec3 reflectDir = reflect(-lightDir, normal);
          float spec = pow(max(dot(viewDir, reflectDir), 0.0), uSpecularPower);
          float fresnel = pow(1.0 - max(dot(viewDir, normal), 0.0), uFresnelPower);
          vec3 specular = uLightColor * spec * uSpecularIntensity * fresnel;

          vec3 color = uSphereColor + ambient + diffuse + specular;
          return pow(color, vec3(uContrast));
        }

        void main() {
          vec2 uv = (gl_FragCoord.xy * 2.0 - uResolution.xy) / uResolution.y;
          vec3 ro = vec3(uv, -1.0);
          vec3 rd = vec3(0.0, 0.0, 1.0);

          float t = rayMarch(ro, rd);
          float distToCursor = length(uv - uCursorSphere.xy);
          float glow = pow(1.0 - smoothstep(0.0, uCursorGlowRadius, distToCursor), 2.0) * uCursorGlowIntensity;

          if (t > 0.0) {
            vec3 p = ro + rd * t;
            vec3 color = lighting(p, rd);
            float fogAmount = 1.0 - exp(-t * uFogDensity);
            color = mix(color, uBackgroundColor, fogAmount * 0.5);
            color += uCursorGlowColor * glow * 0.3;
            gl_FragColor = vec4(color, 1.0);
          } else if (glow > 0.01) {
            gl_FragColor = vec4(uCursorGlowColor, glow * 0.5);
          } else {
            gl_FragColor = vec4(0.0);
          }
        }
      `,
      transparent: true,
    });

    const geometry = new THREE.PlaneGeometry(2, 2);
    const mesh = new THREE.Mesh(geometry, material);
    scene.add(mesh);

    function screenToWorld(normalizedX: number, normalizedY: number) {
      const uvX = normalizedX * 2.0 - 1.0;
      const uvY = normalizedY * 2.0 - 1.0;
      const aspect = width / height;
      return new THREE.Vector3(uvX * aspect, uvY, 0.0);
    }

    function onPointerMove(clientX: number, clientY: number) {
      const rect = container!.getBoundingClientRect();
      const x = (clientX - rect.left) / rect.width;
      const y = 1.0 - (clientY - rect.top) / rect.height;
      targetMousePosition.set(x, y);
    }

    const handleMouseMove = (e: MouseEvent) => onPointerMove(e.clientX, e.clientY);
    const handleTouchMove = (e: TouchEvent) => {
      if (e.touches.length > 0) onPointerMove(e.touches[0].clientX, e.touches[0].clientY);
    };

    container.addEventListener('mousemove', handleMouseMove, { passive: true });
    container.addEventListener('touchmove', handleTouchMove, { passive: true });

    const resizeObserver = new ResizeObserver(() => {
      width = container!.clientWidth || 1;
      height = container!.clientHeight || 1;
      renderer.setSize(width, height);
      material.uniforms.uResolution.value.set(width, height);
    });
    resizeObserver.observe(container);

    let disposed = false;
    let frameId = 0;

    function animate() {
      if (disposed) return;
      frameId = requestAnimationFrame(animate);

      mousePosition.x += (targetMousePosition.x - mousePosition.x) * 0.08;
      mousePosition.y += (targetMousePosition.y - mousePosition.y) * 0.08;

      cursorSphere3D.copy(screenToWorld(mousePosition.x, mousePosition.y));
      material.uniforms.uCursorSphere.value.copy(cursorSphere3D);
      material.uniforms.uTime.value = clock.getElapsedTime();

      renderer.render(scene, camera);
    }
    animate();

    return () => {
      disposed = true;
      cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
      container.removeEventListener('mousemove', handleMouseMove);
      container.removeEventListener('touchmove', handleTouchMove);
      geometry.dispose();
      material.dispose();
      renderer.dispose();
      if (canvas.parentNode === container) container.removeChild(canvas);
    };
  }, []);

  return <div ref={containerRef} aria-hidden="true" className="pointer-events-none absolute inset-0" />;
}
